import { useState, useEffect, useRef } from 'react';
import { IFiltros } from '@/components/templates/search/interfaces/IEstructuraPrincipal';
import { constructUrlFromFilters } from '@/components/templates/filters/util';
import { IBanners } from '@/components/templates/search/interfaces/IEstructuraPrincipal';

interface BannerUpdaterResult {
	banners: IBanners | null;
	breadcrumbs: any | null;
	seo: any | null;
	loading: boolean;
	error: string | null;
}

interface ListaSEOResponse {
	exists: boolean;
	data?: {
		banners: IBanners;
		breadcrumbs?: any;
		seo?: any;
	};
}

/**
 * Hook personalizado que verifica asíncronamente si existe una lista SEO 
 * para filtros específicos y actualiza banners, breadcrumbs y SEO en background
 */
export const useBannerUpdater = (
	filters: IFiltros[], 
	urlSlug?: Array<{ idType: number }>,
	initialBanners?: IBanners,
	initialBreadcrumbs?: any,
	initialSeo?: any
): BannerUpdaterResult => {
	const [banners, setBanners] = useState<IBanners | null>(initialBanners || null);
	const [breadcrumbs, setBreadcrumbs] = useState<any | null>(initialBreadcrumbs || null);
	const [seo, setSeo] = useState<any | null>(initialSeo || null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const isFirstLoad = useRef(true);
	const previousFiltersRef = useRef<string>('');

	useEffect(() => {
		// En la primera carga, usar los valores iniciales y marcar como cargado
		if (isFirstLoad.current) {
			setBanners(initialBanners || null);
			setBreadcrumbs(initialBreadcrumbs || null);
			setSeo(initialSeo || null);
			isFirstLoad.current = false;
			previousFiltersRef.current = JSON.stringify(filters);
			return;
		}

		// Verificar si realmente hubo cambios en los filtros
		const currentFiltersString = JSON.stringify(filters);
		if (previousFiltersRef.current === currentFiltersString) {
			return;
		}
		previousFiltersRef.current = currentFiltersString;

		// Si no hay filtros después de un cambio, usar los valores iniciales
		if (!filters || filters.length === 0) {
			setBanners(initialBanners || null);
			setBreadcrumbs(initialBreadcrumbs || null);
			setSeo(initialSeo || null);
			return;
		}

		const checkForNewBanners = async () => {
			try {
				setLoading(true);
				setError(null);

				// Cancelar request anterior si existe
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}

				// Crear nuevo AbortController
				abortControllerRef.current = new AbortController();

				// Construir URL desde filtros
				const [newUrl] = constructUrlFromFilters(filters, urlSlug) ?? [];
				if (!newUrl) {
					setLoading(false);
					return;
				}

				// Extraer solo el path sin query parameters
				const [urlPath] = newUrl.split('?');
				const cleanPath = urlPath.startsWith('/') ? urlPath.substring(1) : urlPath;

				// console.log('🔍 [useBannerUpdater] Checking for SEO list:', cleanPath);
				
				// Usar la URL base del backend directamente
				const apiBaseUrl = process.env.NEXT_PUBLIC_HOME_BASE_API || '';
				const fullApiUrl = `${apiBaseUrl}/listas/existe/${encodeURIComponent(cleanPath)}`;
				
				// console.log('🔍 [useBannerUpdater] Full URL to check:', fullApiUrl);

				// Verificar si existe una lista para esta URL
				const response = await fetch(fullApiUrl, {
					signal: abortControllerRef.current.signal,
					headers: {
						'Content-Type': 'application/json',
					},
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const result: ListaSEOResponse = await response.json();

				// console.log('📊 [useBannerUpdater] API response:', result);

				if (result.exists && result.data) {
					// console.log('✅ [useBannerUpdater] Found SEO list, updating banners and SEO');
					setBanners(result.data.banners);
					setBreadcrumbs(result.data.breadcrumbs);
					setSeo(result.data.seo);
				} else {
					// console.log('❌ [useBannerUpdater] No SEO list found, keeping original content');
					// Si no existe, mantener los valores originales
					setBanners(initialBanners || null);
					setBreadcrumbs(initialBreadcrumbs || null);
					setSeo(initialSeo || null);
				}
			} catch (err: any) {
				// Si el error es por abort, no hacer nada
				if (err instanceof Error && err.name === 'AbortError') {
					return;
				}
				
				// console.error('❌ [useBannerUpdater] Error checking for content:', err);
				setError(err instanceof Error ? err.message : 'Unknown error');
				
				// En caso de error, mantener los valores originales
				setBanners(initialBanners || null);
				setBreadcrumbs(initialBreadcrumbs || null);
				setSeo(initialSeo || null);
			} finally {
				setLoading(false);
			}
		};

		// Debounce para evitar múltiples llamadas
		const timeoutId = setTimeout(checkForNewBanners, 500);
		
		return () => {
			clearTimeout(timeoutId);
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [filters, urlSlug, initialBanners, initialBreadcrumbs, initialSeo]);

	// Cleanup al desmontar
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	return {
		banners,
		breadcrumbs,
		seo,
		loading,
		error,
	};
}; 